import { Variable } from '~/packages/variable/variables.typing'
import prepareAssertions from '~/packages/variable/prepareAssertions.util'
import { boolean, dateTime, defineAction, enumerated, number, object, string } from '~/prix'
import expression, { Expression } from '~/prix/types/expression'
import { analysis } from '~/prix/types/presets/analysis'
import { assertions } from '~/prix/types/presets/assertions'
import { select } from '~/prix/types/presets/select'
import { childrenType, spaceForPoint } from '~/prix/types/presets/space'
import { timeForIncidence } from '~/prix/types/presets/time'
import { variable } from '~/prix/types/presets/variable'
import monitorAttendanceLegalEntityDistinctStatsAction from './monitorAttendanceEntityDistinctStats.action.json'
import getRecommendedCacheOptions from '~/utils/server/stats/getRecommendedCacheOptions'
import prefixProperties from '~/prix/utils/prefixProperties'
import { defaultDetectionLayers } from '~/utils/server/stats/defaultTimeSeriesLayers'

const emissorDistinctAttendanceLegalEntityShape = {
  id: string().label('ID', 'Identificador', 'Código'),
  cnpj: string().isOptional().label('CNPJ da empresa'),
  attendanceDate: dateTime().isOptional().label('Data do atendimento'),
  attendanceQuantity: number().isOptional().label('Quantidade de atendimentos'),
  createdAt: dateTime().isOptional().label('Data de criação'),
  updatedAt: dateTime().isOptional().label('Data de atualização'),
}

const combinedShape = object({
  ...prefixProperties(
    'emissorDistinctAttendanceLegalEntity.',
    emissorDistinctAttendanceLegalEntityShape,
  ),

  // infos para o clusterList e export
  tradeName: string().isOptional().label('Nome fantasia'),
  corporateName: string().isOptional().label('Razão social'),
  isSimples: boolean().isOptional().label('Simples Nacional'),
  isMei: boolean().isOptional().label('MEI'),
  city: string().isOptional().label('Cidade'),
  state: string().isOptional().label('Estado'),
  ageGroup: enumerated({
    startup: 'Startup',
    young: 'Jovem',
    growth: 'Crescimento',
    established: 'Estabelecida',
  }).label('Maturidade'),
  sizeAbbreviation: enumerated({
    MEI: 'MEI',
    ME: 'ME',
    EPP: 'EPP',
    other: 'Outros',
    unknown: 'Desconhecido',
  }).label('Porte'),
})

export const monitorAttendanceLegalEntityDistinctExpression = expression(combinedShape.shape)

export const monitorAttendanceLegalEntityDistinctVariable: Variable = {
  key: 'monitorAttendanceLegalEntityDistinct',
  categoryKey: 'distinctLegalEntitiesEmissorNfe',
  categoryKeys: ['distinctLegalEntitiesEmissorNfe'],
  actionDefinitionKey: monitorAttendanceLegalEntityDistinctStatsAction.key,
  title: 'Emissor NF-e',
  label: 'Atendimentos de empresas distintas pelo Emissor NF-e',
  unitSingular: 'atendimento',
  unitPlural: 'atendimentos',
  grammaticalGender: 'male',
  timeType: 'incidence',
  shape: combinedShape,
  betterDensity: 'more',
  valueRules: {
    minValue: 0,
  },
  aggregationMode: 'sum',
  customValue: {
    type: 'expression',
    kind: 'function',
    name: 'sum',
    arguments: [
      {
        type: 'expression',
        kind: 'property',
        property: 'emissorDistinctAttendanceLegalEntity.attendanceQuantity',
      },
    ],
  },
  relevance: 0.5,
  basicSelect: ['emissorDistinctAttendanceLegalEntity.cnpj', 'legalEntity.tradeName'],
  detailedSelect: [
    'emissorDistinctAttendanceLegalEntity.id',
    'emissorDistinctAttendanceLegalEntity.cnpj',
    'tradeName',
    'corporateName',
    'sizeAbbreviation',
    'ageGroup',
    'city',
    'state',
    'isSimples',
    'isMei',
    'emissorDistinctAttendanceLegalEntity.attendanceDate',
  ],
  timeSeries: {
    model: 'additive',
    fitDays: 365 * 10,
    layers: [
      ...defaultDetectionLayers,
      { layer: 'calendarSeasonality', type: 'year', degree: 1 },
      {
        layer: 'calendarSeasonality',
        type: 'dayOfWeek',
        degree: 1,
        optimization: {
          minimumDays: 60,
          lossFunction: 'recentWeightedMeanSquaredError',
        },
      },
      { layer: 'calendarSeasonality', type: 'monthOfYear', degree: 0 },
      { layer: 'trend', degree: 1 },
      { layer: 'calendarSeasonality', type: 'holiday', degree: 1 },
      { layer: 'calendarSeasonality', type: 'dayOfMonth', degree: 1 },
      { layer: 'trend', degree: 2 },
    ],
  },
  assertions: prepareAssertions(monitorAttendanceLegalEntityDistinctExpression),
}

const monitorAttendanceLegalEntityDistinctStatsDefinition = defineAction({
  ...monitorAttendanceLegalEntityDistinctStatsAction,
  input: object({
    variable,
    highlight: monitorAttendanceLegalEntityDistinctExpression.isOptional(),
    filter: monitorAttendanceLegalEntityDistinctExpression.isOptional(),
    space: spaceForPoint().isOptional(),
    time: timeForIncidence().isOptional(),
    select: select(combinedShape),
    assertions,
    analysis,
  }),
  output: object({
    items: object({
      value: number(),
      geoId: string().isOptional(),
      geoType: childrenType.isOptional(),
      dateTime: string().isOptional(),
    }),
  }),
  cache: getRecommendedCacheOptions,
})

export default monitorAttendanceLegalEntityDistinctStatsDefinition
